import { z } from 'zod';

/**
 * Filter dates travel as `YYYY-MM-DD` because that is what a URL, a saved
 * filter, and a native date input all hold: short, readable, and free of a
 * timezone that would shift the day when a link is opened elsewhere. The
 * database wants instants, so these convert at that boundary, in local time.
 * Using UTC here would move the day by one for anyone west of Greenwich.
 */

/** Parses `YYYY-MM-DD` to local midnight. Returns null for anything else. */
export const parseCalendarDate = (value: string | undefined): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Rejects a well-formed but impossible date such as 2026-02-31, which the
  // Date constructor would silently roll forward into March.
  return date.getMonth() === Number(month) - 1 ? date : null;
};

/** Start of the given local day, as an ISO instant for a `gte` comparison. */
export const calendarDateToIsoStart = (value: string | undefined): string | undefined => {
  const date = parseCalendarDate(value);
  return date ? date.toISOString() : undefined;
};

/** End of the given local day, as an ISO instant for an `lte` comparison. */
export const calendarDateToIsoEnd = (value: string | undefined): string | undefined => {
  const date = parseCalendarDate(value);
  if (!date) return undefined;
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

/**
 * A filter date in the URL and in storage. Shared so the four listing pages
 * that carry a date range agree on the format.
 */
export const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
